import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { CheckCircle, XCircle, Loader2, Lock, Eye, EyeOff } from "lucide-react";
import { auth } from "../config/firebase";
import { useLanguage } from "../contexts/LanguageContext";

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const isRo = language === "ro";

  const [status, setStatus] = useState<
    "loading" | "ready" | "success" | "error"
  >("loading");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);

  const oobCode = searchParams.get("oobCode");

  // Translations
  const t = {
    ro: {
      loading: "Se verifică linkul...",
      title: "Resetează parola",
      subtitle: "Introdu noua ta parolă",
      newPassword: "Parolă nouă",
      confirmPassword: "Confirmă parola",
      resetButton: "Resetează parola",
      resetting: "Se procesează...",
      success: "Parola a fost schimbată!",
      successDesc:
        "Parola ta a fost resetată cu succes. Acum te poți autentifica cu noua parolă.",
      goToLogin: "Mergi la autentificare",
      errorTitle: "Eroare",
      invalidLink:
        "Linkul de resetare nu este valid sau a expirat. Solicită un nou link.",
      passwordMismatch: "Parolele nu corespund",
      passwordRequirements: "Cerințe parolă:",
      req12chars: "Minim 12 caractere",
      reqUppercase: "Cel puțin o literă mare",
      reqLowercase: "Cel puțin o literă mică",
      reqNumber: "Cel puțin un număr",
      reqSymbol: "Cel puțin un simbol (!@#$%^&*)",
      requestNewLink: "Solicită un nou link",
    },
    it: {
      loading: "Verifica del link in corso...",
      title: "Reimposta la password",
      subtitle: "Inserisci la tua nuova password",
      newPassword: "Nuova password",
      confirmPassword: "Conferma password",
      resetButton: "Reimposta password",
      resetting: "Elaborazione...",
      success: "Password cambiata!",
      successDesc:
        "La tua password è stata reimpostata con successo. Ora puoi accedere con la nuova password.",
      goToLogin: "Vai al login",
      errorTitle: "Errore",
      invalidLink:
        "Il link di reimpostazione non è valido o è scaduto. Richiedi un nuovo link.",
      passwordMismatch: "Le password non corrispondono",
      passwordRequirements: "Requisiti password:",
      req12chars: "Minimo 12 caratteri",
      reqUppercase: "Almeno una lettera maiuscola",
      reqLowercase: "Almeno una lettera minuscola",
      reqNumber: "Almeno un numero",
      reqSymbol: "Almeno un simbolo (!@#$%^&*)",
      requestNewLink: "Richiedi un nuovo link",
    },
  };

  const text = isRo ? t.ro : t.it;

  // Validate oobCode on mount
  useEffect(() => {
    const verifyCode = async () => {
      if (!oobCode) {
        setStatus("error");
        setErrorMessage(text.invalidLink);
        return;
      }

      try {
        // Verify the password reset code and get the email
        const userEmail = await verifyPasswordResetCode(auth, oobCode);
        setEmail(userEmail);
        setStatus("ready");
      } catch (error) {
        console.error("Error verifying reset code:", error);
        setStatus("error");
        setErrorMessage(text.invalidLink);
      }
    };

    verifyCode();
  }, [oobCode, text.invalidLink]);

  // Password validation
  const validatePassword = (pwd: string): string[] => {
    const errors: string[] = [];

    if (pwd.length < 12) {
      errors.push(isRo ? "Minim 12 caractere" : "Minimo 12 caratteri");
    }
    if (!/[A-Z]/.test(pwd)) {
      errors.push(
        isRo ? "Cel puțin o literă mare" : "Almeno una lettera maiuscola",
      );
    }
    if (!/[a-z]/.test(pwd)) {
      errors.push(
        isRo ? "Cel puțin o literă mică" : "Almeno una lettera minuscola",
      );
    }
    if (!/[0-9]/.test(pwd)) {
      errors.push(isRo ? "Cel puțin un număr" : "Almeno un numero");
    }
    if (!/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\/~`]/.test(pwd)) {
      errors.push(
        isRo
          ? "Cel puțin un simbol (!@#$%^&*)"
          : "Almeno un simbolo (!@#$%^&*)",
      );
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate password
    const errors = validatePassword(password);
    if (errors.length > 0) {
      setPasswordErrors(errors);
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage(text.passwordMismatch);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");
    setPasswordErrors([]);

    try {
      await confirmPasswordReset(auth, oobCode!, password);
      setStatus("success");
    } catch (error) {
      console.error("Error resetting password:", error);
      setIsSubmitting(false);
      setErrorMessage(text.invalidLink);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 bg-gray-50 flex items-center">
      <div className="container-custom">
        <div className="max-w-md mx-auto">
          <div className="card p-8">
            {status === "loading" && (
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6">
                  <Loader2 className="w-10 h-10 text-romanian-blue animate-spin" />
                </div>
                <h1 className="font-display text-2xl font-bold text-gray-900 mb-4">
                  {text.loading}
                </h1>
              </div>
            )}

            {status === "ready" && (
              <>
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-romanian-blue/10 rounded-full mb-4">
                    <Lock className="w-8 h-8 text-romanian-blue" />
                  </div>
                  <h1 className="font-display text-2xl font-bold text-gray-900 mb-2">
                    {text.title}
                  </h1>
                  <p className="text-gray-600">{text.subtitle}</p>
                  {email && (
                    <p className="text-sm text-gray-500 mt-2">{email}</p>
                  )}
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Password requirements info */}
                  <div className="bg-gray-50 rounded-lg p-4 text-sm">
                    <p className="font-medium text-gray-700 mb-2">
                      {text.passwordRequirements}
                    </p>
                    <ul className="text-gray-600 space-y-1">
                      <li>• {text.req12chars}</li>
                      <li>• {text.reqUppercase}</li>
                      <li>• {text.reqLowercase}</li>
                      <li>• {text.reqNumber}</li>
                      <li>• {text.reqSymbol}</li>
                    </ul>
                  </div>

                  {/* New Password */}
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {text.newPassword}
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setPasswordErrors([]);
                        }}
                        className="input-field pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label
                      htmlFor="confirmPassword"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {text.confirmPassword}
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="input-field pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Errors */}
                  {passwordErrors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <ul className="text-red-600 text-sm space-y-1">
                        {passwordErrors.map((err, i) => (
                          <li key={i}>• {err}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {errorMessage && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-red-600 text-sm">{errorMessage}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-primary"
                  >
                    {isSubmitting ? text.resetting : text.resetButton}
                  </button>
                </form>
              </>
            )}

            {status === "success" && (
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h1 className="font-display text-2xl font-bold text-gray-900 mb-4">
                  {text.success}
                </h1>
                <p className="text-gray-600 mb-6">{text.successDesc}</p>
                <Link
                  to="/login"
                  className="btn-primary inline-flex items-center space-x-2"
                >
                  <span>{text.goToLogin}</span>
                </Link>
              </div>
            )}

            {status === "error" && (
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
                <h1 className="font-display text-2xl font-bold text-gray-900 mb-4">
                  {text.errorTitle}
                </h1>
                <p className="text-gray-600 mb-6">{errorMessage}</p>
                <Link
                  to="/login"
                  className="btn-primary inline-flex items-center space-x-2"
                >
                  <span>{text.requestNewLink}</span>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
